import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  IconButton,
  Input,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { BrandingContext } from "../../../BrandingProvider";
import axios from "axios";
import { StyledTextFieldBootstrap } from "../../doctor-console/StyledComponents";
import { toast } from "react-toastify";

// SaveS3Resources keys each stored file by its name minus the extension, and
// get_facility_branding returns that key as `name` — so every upload has to
// carry our fixed base name, not whatever the user picked.
const renameForUpload = (file, baseName) => {
  const match = /\.[^.]+$/.exec(file.name || "");
  const extension = match ? match[0] : ".png";
  return new File([file], `${baseName}${extension}`, { type: file.type });
};

const StatementCard = ({
  title,
  description,
  placeholder,
  value,
  onChange,
  isEditing,
  onEdit,
  onCancel,
}) => (
  <Card
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      backgroundColor: "#fff",
      borderRadius: 2,
      border: "1px solid #e6e9f0",
      boxShadow: "0px 2px 8px rgba(40, 59, 106, 0.08)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0px 8px 18px rgba(40, 59, 106, 0.14)",
      },
    }}
  >
    <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#283b6a", lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mt: 0.25 }}
          >
            {description}
          </Typography>
        </Box>
        <IconButton
          size="small"
          title={isEditing ? "Cancel" : "Edit"}
          onClick={isEditing ? onCancel : onEdit}
          sx={{ color: "#283b6a" }}
        >
          {isEditing ? (
            <CancelIcon fontSize="small" />
          ) : (
            <EditIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      <Box sx={{ mt: 2 }}>
        {isEditing ? (
          <>
            <StyledTextFieldBootstrap
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              // The base style is fixed-height for single-line use (45px root,
              // 50px input), which clips a growing textarea over the outline.
              sx={{
                height: "auto",
                marginBottom: 0,
                "& .MuiOutlinedInput-root": {
                  height: "auto",
                  alignItems: "flex-start",
                  padding: "12px 14px",
                },
                "& .MuiInputBase-inputMultiline": {
                  padding: 0,
                  lineHeight: 1.6,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                textAlign: "right",
                mt: 0.5,
              }}
            >
              {value.length} characters
            </Typography>
          </>
        ) : value ? (
          <Box
            sx={{
              borderLeft: "3px solid #21cdc0",
              backgroundColor: "#f7f9fc",
              borderRadius: "0 4px 4px 0",
              px: 2,
              py: 1.5,
              minHeight: 84,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.6,
                color: "#2f3442",
              }}
            >
              {value}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              border: "1px dashed #c9d1e0",
              borderRadius: 1,
              px: 2,
              py: 1.5,
              minHeight: 84,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Nothing set yet — select the pencil to add a statement.
            </Typography>
          </Box>
        )}
      </Box>
    </CardContent>
  </Card>
);

const BrandingManagement = () => {
  const brandingData_temp = useContext(BrandingContext);
  const brandingData = !Array.isArray(brandingData_temp)
    ? []
    : brandingData_temp;
  // const brandingData =
  //   typeof brandingData_temp !== "string" ? brandingData_temp : [];
  console.log("brandingData_temp", brandingData_temp);
  // const brandingData =
  //   brandingData_temp.status !== "Failed" ? brandingData_temp : [];
  console.log("branding data", brandingData);
  // "facility_name" holds the facility_id slug (set from userInfo.facility_id).
  const facility_id = sessionStorage.getItem("facility_name");
  const [editingField, setEditingField] = useState(null); // To track which field is in edit mode
  const [collateralImage, setCollateralImage] = useState(null);
  const [emailBanner, setEmailBanner] = useState(null);
  const [emailBrandStatement, setEmailBrandStatement] = useState("");
  const [collateralBrandStatement, setCollateralBrandStatement] = useState("");
  const [collateralImageError, setCollateralImageError] = useState("");
  const [themeColour, setThemeColour] = useState("");
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };

  console.log("editingField", editingField);
  console.log("email brand statement", emailBrandStatement);

  useEffect(() => {
    const collateralImageItem = brandingData?.find(
      (item) => item.name === "collateral_image"
    );
    const existingCollateralImage = collateralImageItem?.content || null;

    const emailBannerItem = brandingData?.find(
      (item) => item.name === "email_banner"
    );
    const existingEmailBanner = emailBannerItem?.content || null;

    const brandingItem = brandingData?.find(
      (item) => item.name === "branding_statements"
    );
    const brandingContent = brandingItem?.content || {};

    // Initial branding data
    const initialEmailBrandStatement =
      brandingContent.emailBrandStatement || "Default Email Statement";
    setEmailBrandStatement(initialEmailBrandStatement);
    const initialCollateralBrandStatement =
      brandingContent.collateralBrandStatement ||
      "Default Collateral Statement";
    setCollateralBrandStatement(initialCollateralBrandStatement);
    const initialThemeColour = brandingContent.themeColour || "#21cdc0";
    setThemeColour(initialThemeColour);
  }, [brandingData_temp]);

  const collateralImageItem = brandingData?.find(
    (item) => item.name === "collateral_image"
  );
  const existingCollateralImage = collateralImageItem?.content || null;

  const emailBannerItem = brandingData?.find(
    (item) => item.name === "email_banner"
  );
  const existingEmailBanner = emailBannerItem?.content || null;

  const brandingItem = brandingData?.find(
    (item) => item.name === "branding_statements"
  );
  const brandingContent = brandingItem?.content || {};

  const handleCollateralImageChange = (event) => {
    const file = event.target.files[0];
    const minSize = 20 * 1024; // 20KB
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (file) {
      // Validate file type
      if (file.type !== "image/png") {
        toast.error("Only PNG files are allowed.");
        setEditingField(null);
        return;
      }

      // Validate file size
      if (file.size < minSize) {
        toast.error("File size must be at least 20KB.");
        setEditingField(null);
        return;
      }
      if (file.size > maxSize) {
        toast.error("File size must not exceed 10MB.");
        setEditingField(null);
        return;
      }

      // If all validations pass
      setCollateralImage(file);
    }
  };

  const handleEmailBannerChange = (event) => {
    const file = event.target.files[0];
    const minSize = 20 * 1024; // 20KB
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (file) {
      // Validate file type
      if (file.type !== "image/png") {
        toast.error("Only PNG files are allowed.");
        setEditingField(null);
        return;
      }

      // Validate minimum file size
      if (file.size < minSize) {
        toast.error("File size must be at least 20KB.");
        setEditingField(null);
        return;
      }

      // Validate maximum file size
      if (file.size > maxSize) {
        toast.error("File size must not exceed 10MB.");
        setEditingField(null);
        return;
      }

      // If all validations pass
      setEmailBanner(file);
    }
  };

  const startEditing = (field) => {
    setEditingField(field);
  };

  const stopEditing = () => {
    setEditingField(null);
  };

  const saveChanges = async () => {
    const formData = new FormData();
    // formData.append("functionality", "branding");
    formData.append("bucket_type", "license");
    formData.append("bucket_category", "conf");
    if (facility_id) {
      formData.append("facility_id", facility_id);
    }

    // Only changed images are uploaded — anything not sent keeps its stored file.
    if (emailBanner) {
      formData.append(
        "file_object",
        renameForUpload(emailBanner, "email_banner")
      );
    }

    if (collateralImage) {
      formData.append(
        "file_object",
        renameForUpload(collateralImage, "collateral_image")
      );
    }

    // Include branding statements
    const brandingStatements = {
      emailBrandStatement: emailBrandStatement,
      collateralBrandStatement: collateralBrandStatement,
      themeColour: themeColour,
    };

    const brandingStatementsBlob = new Blob(
      [JSON.stringify(brandingStatements)],
      { type: "application/json" }
    );
    const brandingStatementsFile = new File(
      [brandingStatementsBlob],
      "branding_statements.json",
      { type: "application/json" }
    );
    formData.append("file_object", brandingStatementsFile);

    // Log the payload (FormData) for debugging
    for (const [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
    // try {
    //   await axios.post(
    //     `${process.env.REACT_APP_BACKEND_ROUTE_URL}save_facility_branding/`,
    //     formData,
    //     {
    //       headers: {
    //         "Content-Type": "multipart/form-data",
    //         Authorization: `Bearer ${authToken}`,
    //       },
    //     }
    //   );
    //   toast.success("Branding data uploaded successfully!");
    //   stopEditing(); // Close edit mode on successful save
    // } catch (error) {
    //   console.error("Error uploading branding data:", error);
    // }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}save_facility_branding/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      // Handle server response
      if (response.status === 200) {
        const responseData = response.data;
        console.log("Server Response:", responseData);

        // Check if server returned success confirmation
        if (responseData.status === "Success") {
          toast.success(
            responseData.message || "Branding data uploaded successfully!"
          );
          stopEditing();
          window.location.reload(); // Close edit mode on successful save
        } else {
          const message = response.data.message || "An unknown error occurred.";

          if (message.includes("corrupted")) {
            toast.error(
              "The Facility documents uploaded are corrupted. Please try with a different document."
            );
          } else if (message.includes("Virus")) {
            toast.error(
              "The Facility documents uploaded has virus. Please try with a different document."
            );
          } else {
            toast.error(message);
          }
        }
      } else {
        toast.error(`Unexpected response: ${response.status}`);
        console.warn("Unexpected server response:", response);
      }
    } catch (error) {
      // Handle error response
      if (error.response) {
        // Server responded with a status other than 2xx
        console.error("Server Error Response:", error.response);
        toast.error(
          `Error: ${
            error.response.data?.message || "Failed to upload branding data."
          }`
        );
      } else {
        // Something else caused the error
        console.error("Error occurred:", error.message);
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
  };

  return (
    <Box sx={{ padding: 4, maxWidth: "100%", backgroundColor: "#f8f9fa" }}>
      <Grid item xs={12} container justifyContent="center">
        <Typography variant="h4" gutterBottom>
          Branding Management
        </Typography>
      </Grid>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "#283b6a;",
              color: "#ffffff", // Ensure text contrast
              padding: "10px 20px",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            <Typography variant="h5" gutterBottom style={{ color: "#ffffff" }}>
              Branding Data
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatementCard
            title="Email Brand Statement"
            description="Shown at the foot of emails sent to patients."
            placeholder="e.g. Thank you for choosing our hospital."
            value={emailBrandStatement}
            onChange={(e) => setEmailBrandStatement(e.target.value)}
            isEditing={editingField === "emailBrandStatement"}
            onEdit={() => startEditing("emailBrandStatement")}
            onCancel={stopEditing}
          />
        </Grid>

        {/* Collateral Brand Statement */}

        <Grid item xs={12} sm={6} md={4}>
          <StatementCard
            title="Collateral Brand Statement"
            description="Shown on printed collateral such as invoices and reports."
            placeholder="e.g. Caring for your family since 1998."
            value={collateralBrandStatement}
            onChange={(e) => setCollateralBrandStatement(e.target.value)}
            isEditing={editingField === "collateralBrandStatement"}
            onEdit={() => startEditing("collateralBrandStatement")}
            onCancel={stopEditing}
          />
        </Grid>

        {/* Theme Colour */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              height: "100%",
              backgroundColor: "#fff",
              borderRadius: 2,
              border: "1px solid #e6e9f0",
              boxShadow: "0px 2px 8px rgba(40, 59, 106, 0.08)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0px 8px 18px rgba(40, 59, 106, 0.14)",
              },
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#283b6a", lineHeight: 1.3 }}
              >
                Theme Colour
              </Typography>
              {editingField === "themeColour" ? (
                <input
                  type="color"
                  value={themeColour} // This will show the current or initial color
                  onChange={(e) => setThemeColour(e.target.value)} // Updates the theme colour in state
                  style={{
                    width: "100%",
                    height: "40px",
                    border: "none",
                    padding: "5px",
                    cursor: "pointer",
                  }}
                />
              ) : (
                <Typography variant="body1">{themeColour}</Typography>
              )}
            </CardContent>
            <Box display="flex" justifyContent="space-between">
              {editingField === "themeColour" ? (
                <IconButton onClick={stopEditing}>
                  <CancelIcon />
                </IconButton>
              ) : (
                <IconButton onClick={() => startEditing("themeColour")}>
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Image Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "#283b6a;",
              color: "#ffffff", // Ensure text contrast
              padding: "10px 20px",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            <Typography variant="h5" gutterBottom style={{ color: "#ffffff" }}>
              Images
            </Typography>
          </Box>
          <Typography
            variant="subtitle2"
            gutterBottom
            style={{ color: "#555", fontStyle: "italic" }}
          >
            NOTE: The email banner (3000×1000 px) and collateral image (400×100
            px) will be used in customer emails and marketing materials
            respectively. Ensure both reflect your brand and are between 20KB
            and 10MB in size.
          </Typography>
        </Grid>

        {/* Collateral Image */}
        <Grid item xs={12} sm={6}>
          <Card
            sx={{
              backgroundColor: "#fff",
              paddingTop: "20px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "scale(1.05) rotateX(5deg) rotateY(5deg)",
                boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.3)",
              },
            }}
          >
            <CardMedia
              component="img"
              height="200"
              image={`data:image/png;base64,${existingCollateralImage}`}
              alt="Collateral Image"
              sx={{ objectFit: "contain", width: "100%", height: "150px" }}
            />

            <CardContent>
              <Typography variant="h6">Collateral Image</Typography>
              {editingField === "collateralImage" ? (
                <Input type="file" onChange={handleCollateralImageChange} />
              ) : (
                <Typography variant="body1">PNG Image</Typography>
              )}
            </CardContent>

            <Box display="flex" justifyContent="space-between">
              {editingField === "collateralImage" ? (
                <IconButton onClick={stopEditing}>
                  <CancelIcon />
                </IconButton>
              ) : (
                <IconButton onClick={() => startEditing("collateralImage")}>
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Email Banner */}
        <Grid item xs={12} sm={6}>
          <Card
            sx={{
              backgroundColor: "#fff",
              paddingTop: "20px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "scale(1.05) rotateX(5deg) rotateY(5deg)",
                boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.3)",
              },
            }}
          >
            <CardMedia
              component="img"
              height="200"
              image={`data:image/png;base64,${existingEmailBanner}`}
              alt="Email Banner"
              sx={{ objectFit: "contain", width: "100%", height: "150px" }}
            />
            <CardContent>
              <Typography variant="h6">Email Banner</Typography>
              {editingField === "emailBanner" ? (
                <Input type="file" onChange={handleEmailBannerChange} />
              ) : (
                <Typography variant="body1">PNG Image</Typography>
              )}
            </CardContent>
            <Box display="flex" justifyContent="space-between">
              {editingField === "emailBanner" ? (
                <IconButton onClick={stopEditing}>
                  <CancelIcon />
                </IconButton>
              ) : (
                <IconButton onClick={() => startEditing("emailBanner")}>
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Save and Cancel Buttons */}

        {editingField && (
          <Grid
            container
            sx={{ marginTop: 2, justifyContent: "center", gap: 2 }}
          >
            <Grid item xs={6} sm={3}>
              <button
                type="button"
                className="btn btn__primary btn__rounded btn__block"
                onClick={saveChanges}
              >
                <span>Save</span> <i className="icon-arrow-right"></i>
              </button>
            </Grid>

            <Grid item xs={6} sm={3}>
              <button
                type="button"
                className="btn btn__secondary btn__rounded btn__block"
                onClick={stopEditing}
              >
                <span>Cancel</span> <i className="icon-arrow-right"></i>
              </button>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default BrandingManagement;
